// FLAGS: none
// Generic read/edit dropdown for one note field; the caller owns what the chosen value means and does.
import { MenuItem, Select } from "@mui/material";
import EditableDisplay from "@src/views/ClientPage/SessionNotes/components/EditableDisplay";
import {
  EditingContext,
  NoteSections,
} from "@src/views/ClientPage/SessionNotes/EditingContext";
import { useContext } from "react";

const CLICKABLE_FIELD_CLASS =
  "w-full p-1 body1 text-indigo-800 hover:bg-slate-100 hover:cursor-pointer";
interface EditableSelectFieldProps<T extends string | number> {
  section: NoteSections;
  currentValue: T | null | undefined;
  displayValue: string | JSX.Element;
  options: Array<{ label: string; value: T }>;
  onSave: (val: T) => Promise<void>;
  "data-testid"?: string;
}

const EditableSelectField = <T extends string | number>({
  section,
  currentValue,
  displayValue,
  options,
  onSave,
  "data-testid": testId,
}: EditableSelectFieldProps<T>) => {
  const ctx = useContext(EditingContext)!;
  const { editingSection, closeEditing, handleSectionChange } = ctx;

  if (editingSection !== section) {
    return (
      <EditableDisplay
        className={CLICKABLE_FIELD_CLASS}
        onActivate={() => handleSectionChange(section)}
        data-testid={testId}
      >
        {displayValue}
      </EditableDisplay>
    );
  }

  return (
    <Select
      name={section.toString()}
      id={section.toString()}
      defaultOpen
      className="w-full"
      variant="standard"
      value={currentValue ?? ""}
      onChange={async (e) => {
        const newVal = e.target.value as T;
        await onSave(newVal);
        closeEditing();
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};
export default EditableSelectField;
